import { useState } from "react";

export type DiskRateUnit = "KB/s" | "MB/s";

/**
 * The disk chart's unit, with hysteresis. Disk throughput arrives in MB/s, and an idle disk moving a
 * few KB a second reads as 0.0035 on an MB/s axis, so under 1 MB/s a chart uses KB/s. Once picked,
 * the unit only changes when the peak crosses well past that line (above 2 MB/s, below 0.5 MB/s):
 * re-picking at exactly 1 MB/s flipped the title every poll for a disk hovering around it.
 */
function nextDiskUnit(current: DiskRateUnit | null, peakMb: number): DiskRateUnit {
  if (current === "KB/s" && peakMb > 2) {
    return "MB/s";
  }
  if (current === "MB/s" && peakMb < 0.5) {
    return "KB/s";
  }
  if (current !== null) {
    return current;
  }
  return peakMb < 1 ? "KB/s" : "MB/s";
}

/**
 * The unit a disk chart shows for series peaking at `peakMb` MB/s, held between renders (see
 * nextDiskUnit). Only `settled` data moves the held unit — pass false while a previous range's
 * series is standing in for the one being fetched.
 */
export function useDiskRateUnit(peakMb: number, settled: boolean): DiskRateUnit {
  const [shownUnit, setShownUnit] = useState<DiskRateUnit | null>(null);
  const unit = nextDiskUnit(shownUnit, peakMb);
  if (settled && unit !== shownUnit) {
    setShownUnit(unit);
  }
  return unit;
}

/** Multiplier from MB/s to `unit`. */
export function diskRateScale(unit: DiskRateUnit): number {
  return unit === "KB/s" ? 1024 : 1;
}

/** Highest single reading across the given MB/s series, 0 when there is none. */
export function peakDiskRate(...series: { value: number }[][]): number {
  let peak = 0;
  for (const points of series) {
    for (const point of points) {
      peak = Math.max(peak, point.value);
    }
  }
  return peak;
}
